// One flagged test on the Timeline: name, latest value + unit, the lab's flag, above/within/below the lab range,
// the change since the first result in the range, and a small line of the results in the range.
// Every number is the patient's own result; nothing is estimated. Look: Signal DS tokens via Tailwind classes.
import { Checkbox, Sparkline } from '@scripsteam/scrips-signal-ds';
import { SPARKLINE_MIN_POINTS } from '@/patients/lib/lab-results';
import {
  changeSinceFirst,
  changeText,
  formatDay,
  rangePosition,
  rangePositionText,
  type LabTrendCardModel,
  type TimelineRange,
} from '@/patients/lib/lab-trend-cards';
import { FlagChip } from '../labs/lab-analyte-row';
import { useCardPoints } from './use-card-points';

export interface LabTrendCardProps {
  patientId: string;
  card: LabTrendCardModel;
  labsByRoot: ReadonlyMap<string, string>;
  range: TimelineRange;
  /** Compare mode: the card shows a "Compare" box. */
  comparing: boolean;
  selected: boolean;
  /** False once 2 tests are picked: an unpicked card cannot be added. */
  canSelect: boolean;
  onSelectedChange: (selected: boolean) => void;
}

export function LabTrendCard({
  patientId,
  card,
  labsByRoot,
  range,
  comparing,
  selected,
  canSelect,
  onSelectedChange,
}: LabTrendCardProps) {
  const { row } = card;
  const { points } = useCardPoints(patientId, card, labsByRoot, range);
  const values = points.map((p) => p.value);
  const latest = values.length > 0 ? values[values.length - 1] : null;
  const position = latest === null ? null : rangePosition(latest, row.refLow, row.refHigh);
  const change = changeText(changeSinceFirst(points), row.unit);
  const band = row.refLow !== null && row.refHigh !== null ? { low: row.refLow, high: row.refHigh } : undefined;
  const critical = row.flag?.critical === true;

  return (
    <article
      data-testid="lab-trend-card"
      data-critical={critical ? 'true' : undefined}
      className="flex min-w-0 flex-col gap-2 rounded-md border border-border-default bg-surface-card p-3"
      aria-label={`${row.display} trend`}
    >
      <header className="flex items-start justify-between gap-2">
        <h4 className="min-w-0 truncate text-sm font-medium text-text-primary" title={row.display}>
          {row.display}
        </h4>
        <FlagChip flag={row.flag} />
      </header>

      <p className="flex items-baseline gap-1">
        <span
          data-testid="lab-trend-value"
          className={critical ? 'text-lg font-bold text-status-error' : 'text-lg font-semibold text-text-primary'}
        >
          {row.value}
        </span>
        {row.unit ? <span className="text-xs text-text-muted">{row.unit}</span> : null}
      </p>

      <p className="text-xs text-text-secondary">
        {[rangePositionText(position), row.range ? `Range ${row.range}` : null].filter(Boolean).join(' · ') ||
          'No lab range'}
      </p>

      {values.length >= SPARKLINE_MIN_POINTS ? (
        <span data-testid="lab-trend-line" className="block">
          <Sparkline
            data={values}
            height={28}
            stretch
            band={band}
            domain={band ? { low: Math.min(band.low, ...values), high: Math.max(band.high, ...values) } : undefined}
            emphasizeLast
            aria-label={`${row.display}, ${values.length} results in this period`}
          />
        </span>
      ) : null}

      <p className="text-xs text-text-muted">
        {change ?? `Latest ${formatDay(card.date)}`}
      </p>

      {comparing ? (
        <Checkbox
          label="Compare"
          checked={selected}
          disabled={!selected && !canSelect}
          onCheckedChange={onSelectedChange}
        />
      ) : null}
    </article>
  );
}
